import { Component, OnInit, OnDestroy, signal } from '@angular/core'
import { FormBuilder, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms'
import { Router } from '@angular/router'
import { CognitoService } from '../../common/api/services/cognito.service'
import { WIRService } from '../../common/api/services/wir.service'
import { languageContent } from '../../common/json/counselling-mode-content'
import { GtmImplementation } from '../../common/api/services/gtmImplementation'
import { HeaderComponent } from '../../common/component/header/header.component';
import { MatChipListbox, MatChipOption } from '@angular/material/chips';
@Component({
    selector: 'app-counselling-mode',
    templateUrl: './counselling-mode.component.html',
    styleUrls: ['./counselling-mode.component.css'],
    imports: [HeaderComponent, FormsModule, ReactiveFormsModule, MatChipListbox, MatChipOption]
})
export class CounsellingModeComponent implements OnInit, OnDestroy {
  landingFormGroup: any
  showLoader = signal(false)
  submitted: any
  notSelected: any
  isCounsellingModeSelected: any
  officeDetails: any
  counsellorCountry: any
  counsellorCountryCode: any
  defaultLanguage = 'EN'
  selectedLanguage: any = 'EN'
  welcomeText: any
  greatToHaveYou: any
  modeOfCounselling: any
  nextButton: any
  validationErrorText: any
  authReady = signal(false) // Controls visibility — hides page until auth completes
  private tokenRefreshTimeout: any = null

  constructor (
    public formBuilder: FormBuilder,
    private route: Router,
    public cognitoService: CognitoService,
    private WIRService: WIRService,
    public gtmImplementation: GtmImplementation
  ) {
    this.landingFormGroup = this.formBuilder.group({
      counsellingMode: ['', Validators.required]
    })
  }

  ngOnInit (): void {
    // Only clear state on a fresh visit (no sfResponse means first time in the flow)
    // When navigating back from signin, sfResponse and counsellingMode already exist
    const isReturning = !!localStorage.getItem('counsellingMode')
    if (!isReturning) {
      localStorage.removeItem('languageSelected')
      localStorage.removeItem('counsellingMode')
    }
    this.initializeAuth(isReturning)
  }

  ngOnDestroy (): void {
    if (this.tokenRefreshTimeout) {
      clearTimeout(this.tokenRefreshTimeout)
      this.tokenRefreshTimeout = null
    }
  }

  async initializeAuth (isReturning: boolean = false): Promise<void> {
    try {
      await this.cognitoService.initialize()

      if (isReturning) {
        // User is returning via browser back — already authenticated from previous visit.
        // Skip auth checks entirely to avoid SSO redirect on full page reload.
        this.getSFDetails()
        this.getLanguageContent(this.defaultLanguage)
        this.authReady.set(true)
        return
      }

      if (this.cognitoService.isAuthenticated()) {
        await this.cognitoService.getAccessToken()
        this.forSfDetails()
        this.startTokenRefreshTimer()
        this.getSFDetails()
        this.getLanguageContent(this.defaultLanguage)
        this.gtmImplementation.pageView('landing_page', 'anonymous_user', 'na')
        // Auth complete — show the page content
        this.authReady.set(true)
      } else {
        // Not authenticated — redirect to Azure AD login.
        // Keep authReady = false so the page stays hidden (no glitch).
        await this.cognitoService.getAccessToken()
      }
    } catch (error) {
      console.error('Auth initialization failed:', error)
      // Redirect to SSO login on any auth failure
      await this.cognitoService.loginRedirect()
    }
  }

  get f (): any { return this.landingFormGroup.controls }

  // This method starts a timer to silently refresh the Azure AD token every 28 minutes
  startTokenRefreshTimer (): any {
    this.tokenRefreshTimeout = setTimeout(() => {
      void this.cognitoService.getAccessToken().then(() => {
        this.startTokenRefreshTimer()
      }).catch((error: any) => {
        console.warn('Token refresh failed, will retry on next interaction:', error)
        // Don't navigate to error — the auth interceptor will handle 401 on next API call
      })
    }, 1680000)
  }

  // This is used get the selected mode of counselling
  counsellingModeSelected (): any {
    if (this.landingFormGroup.value.counsellingMode) {
      this.isCounsellingModeSelected = true
      localStorage.setItem('counsellingMode', this.landingFormGroup.value.counsellingMode)
    } else {
      this.isCounsellingModeSelected = false
    }
  }

  // This is used to navigate to next page and get office details
  signinPage (): any {
    this.submitted = true
    if (this.landingFormGroup.value.counsellingMode) {
      this.showLoader.set(true)
      this.notSelected = false
      this.gtmImplementation.trackEvents('wir_landing_page', 'next_button', 'next', 'landing_page', 'anonymous_user', 'na')
      // This method is used to get the office details and country details
      this.WIRService.getSfAPI(this.landingFormGroup.value.counsellingMode).subscribe((data: any) => {
        localStorage.setItem('sfResponse', JSON.stringify(data))
        localStorage.setItem('counsellingMode', this.landingFormGroup.value.counsellingMode)
        this.route?.navigateByUrl('/signin')
      }, (error: any) => {
        console.log(error)
        this.showLoader.set(false)
        this.route?.navigateByUrl('/error')
      })
    } else {
      this.notSelected = true
    }
  }

  // This is used to navigate to next page and get office details
  forSfDetails (): any {
    this.WIRService.getSfAPI('Virtual').subscribe((data: any) => {
      localStorage.setItem('sfResponse', JSON.stringify(data))
    })
  }

  // This method is used to get the office details and country details
  getSFDetails (): any {
    const sfResponse: any = localStorage.getItem('sfResponse')
    this.officeDetails = JSON.parse(sfResponse)
    this.counsellorCountry = this.officeDetails?.wir_cognito_country
    this.counsellorCountryCode = this.officeDetails?.wir_country_code
  }

  // Based on language change it will show the form.
  languageChanged (event: any): any {
    this.selectedLanguage = event.value
    this.getLanguageContent(event.value)
  }

  // This is used to get static text from language changed.
  getLanguageContent (language: any): any {
    if (language === this.defaultLanguage) {
      this.welcomeText = languageContent[language]?.welcomeText
      this.greatToHaveYou = languageContent[language]?.greatToHaveYou
      this.modeOfCounselling = languageContent[language]?.modeOfCounselling
      this.nextButton = languageContent[language]?.nextButton
      this.validationErrorText = languageContent[language]?.validationError
    } else {
      this.welcomeText = languageContent[this.counsellorCountryCode + '_' + language]?.welcomeText
      this.greatToHaveYou = languageContent[this.counsellorCountryCode + '_' + language]?.greatToHaveYou
      this.modeOfCounselling = languageContent[this.counsellorCountryCode + '_' + language]?.modeOfCounselling
      this.nextButton = languageContent[this.counsellorCountryCode + '_' + language]?.nextButton
      this.validationErrorText = languageContent[this.counsellorCountryCode + '_' + language]?.validationError
    }
  }
}
